import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

interface CTASectionProps {
  title: string;
  description: string;
  primaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  secondaryCTA?: {
    text: string;
    link: string;
    isExternal?: boolean;
  };
  backgroundClass?: string;
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  description,
  primaryCTA,
  secondaryCTA,
  backgroundClass = "bg-primary",
}) => {
  // Determine if we're using a light background (for text color contrast)
  const isLightBackground = backgroundClass.includes("from-[#EDF5E9]") || 
                           backgroundClass.includes("bg-joerg-50") ||
                           backgroundClass.includes("bg-joerg-100") ||
                           backgroundClass.includes("bg-white") ||
                           backgroundClass.includes("bg-sage-") ||
                           backgroundClass.includes("bg-warm-white");

  const renderCTALink = (cta: { text: string; link: string; isExternal?: boolean }, isPrimary: boolean) => {
    // Adjust button styles based on background type
    const buttonClassNames = `px-6 py-3 rounded-full inline-flex items-center justify-center transition-colors shadow-md hover:shadow-lg font-medium ${
      isPrimary
        ? isLightBackground
          ? 'bg-amber-500 text-white hover:bg-amber-600'
          : 'bg-white text-primary hover:bg-gray-100'
        : isLightBackground
          ? 'bg-transparent border border-primary text-primary hover:bg-primary/5'
          : 'bg-transparent border border-white/70 text-white hover:bg-white/10'
    }`;

    if (cta.isExternal) {
      return (
        <a
          href={cta.link}
          className={buttonClassNames}
          target="_blank"
          rel="noopener noreferrer"
        >
          {cta.text}
          <ArrowRight className="ml-2 h-4 w-4" />
        </a>
      );
    }

    return (
      <Link
        to={cta.link}
        className={buttonClassNames}
      >
        {cta.text}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Link>
    );
  };

  // Add appropriate text color class based on background
  const textColorClass = isLightBackground 
    ? 'text-joerg-800' 
    : 'text-white';

  return (
    <section className={`section-spacing ${backgroundClass} relative`}>
      <div className="container">
        <div className="max-w-3xl mx-auto text-left">
          <h2 className={`text-3xl md:text-4xl font-heading mb-6 ${textColorClass}`}>
            {title}
          </h2>
          
          <p className={`mb-8 text-lg ${
            isLightBackground 
              ? 'text-joerg-700' 
              : 'text-white/90'
          }`}>
            {description}
          </p>
          
          <div className="flex flex-col sm:flex-row justify-start gap-4">
            {primaryCTA && renderCTALink(primaryCTA, true)}
            {secondaryCTA && renderCTALink(secondaryCTA, false)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;